"use client";

import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useRouter, usePathname } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from "@/components/shadcn/dialog";
import { useRemoteData, DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { EnvService } from "@/services/Environment/Environment";
import { SpotToast } from "@/components/SpotToast";
import {
  CaseManagementService,
  IntegrationInfo,
  IntegrationsService,
} from "@/generated";
import { useInboxes } from "../useInboxes";
import {
  WizardState,
  WizardStep,
  WIZARD_STEPS,
  InboxSource,
  InboxSourceConfig,
  InboxUseCase,
} from "./types";
import { INBOX_USE_CASES } from "./config";
import {
  IntroStep,
  HowItWorksStep1,
  HowItWorksStep2,
  HowItWorksStep3,
  SourceStep,
  SourceConfigStep,
  UseCaseStep,
  ContextStep,
  // ReviewStep,
  DoneStep,
} from "./steps";
import { INBOX_SOURCES, isSourceConnected } from "./config";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface CreateInboxWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const getInitialState = (): WizardState => ({
  source: null,
  sourceConfig: {},
  useCase: null,
  context: {},
});

export const CreateInboxWizard = ({
  isOpen,
  onClose,
}: CreateInboxWizardProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { spotGroup } = useSpotUser();
  const { createInbox, isCreating } = useInboxes();
  const [currentStep, setCurrentStep] = useState<WizardStep>("intro");
  const [state, setState] = useState<WizardState>(getInitialState());
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [templateConfigData, setTemplateConfigData] = useState<any>(null);
  const [createdInboxId, setCreatedInboxId] = useState<string | null>(null);
  const templateForm = useForm<Record<string, any>>();

  const { data: integrations } = useRemoteData(
    DATA_KEYS.INTEGRATIONS,
    { path: { group_id: spotGroup?.id || "" } },
    { enabled: !!spotGroup?.id }
  );

  const { data: integrationsConfig } = useRemoteData(
    DATA_KEYS.INTEGRATIONS_CONFIG,
    { path: { group_id: spotGroup?.id || "" } },
    { enabled: !!spotGroup?.id }
  );

  const INTEGRATIONS = useMemo(() => {
    const list = (integrationsConfig as { integrations?: IntegrationInfo[] })
      ?.integrations;
    if (!list) return {} as Record<string, IntegrationInfo>;
    return Object.fromEntries(list.map((i) => [i.type, i]));
  }, [integrationsConfig]);

  const currentStepIndex = WIZARD_STEPS.indexOf(currentStep);
  const sourceConnected = isSourceConnected(state.source, integrations || null);

  const useCaseConfig = useMemo(() => {
    return INBOX_USE_CASES.find((u: any) => u.id === state.useCase);
  }, [state.useCase]);

  // Debounced slug for platform-wide uniqueness check
  const [debouncedSlug, setDebouncedSlug] = useState("");
  useEffect(() => {
    const slug = state.sourceConfig.slug;
    if (!slug || slug.length < 3) {
      setDebouncedSlug("");
      return;
    }
    const timer = setTimeout(() => setDebouncedSlug(slug), 300);
    return () => clearTimeout(timer);
  }, [state.sourceConfig.slug]);

  const { data: slugCheck } = useQuery({
    queryKey: ["check-slug", debouncedSlug],
    queryFn: () =>
      CaseManagementService.checkSlug({
        path: { group_id: spotGroup?.id || "" },
        query: { slug: debouncedSlug },
      }),
    enabled: !!spotGroup?.id && !!debouncedSlug,
  });

  const slugError = useMemo(() => {
    const slug = state.sourceConfig.slug;
    if (!slug || state.source !== "noxus") return null;
    if (slug.length < 3) return "Slug must be at least 3 characters.";
    if (slug.startsWith("-") || slug.endsWith("-"))
      return "Slug cannot start or end with a hyphen.";
    if (/--/.test(slug)) return "Slug cannot contain consecutive hyphens.";
    if (slugCheck?.data && !(slugCheck.data as any).available)
      return "This slug is already in use by another inbox.";
    return null;
  }, [state.sourceConfig.slug, state.source, slugCheck]);

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case "intro":
        return true;
      case "source":
        return state.source !== null;
      case "source_config":
        if (state.source === "noxus") {
          return !!state.sourceConfig.slug && !slugError;
        }
        return true;
      case "use_case":
        return true;
      case "context":
        return templateConfigData?.ready !== false;
      default:
        return false;
    }
  }, [currentStep, state, slugError, templateConfigData]);

  const handleConnect = async (sourceOverride?: InboxSource | null) => {
    const sourceToUse = sourceOverride || state.source;
    if (!sourceToUse || !spotGroup) return;

    const sourceConfig = INBOX_SOURCES.find(
      (s: InboxSourceConfig) => s.id === sourceToUse
    );
    if (!sourceConfig?.integration) return;

    const info = INTEGRATIONS[sourceConfig.integration];
    if (!info) return;

    // Find the first available provider for this integration
    const provider = info.providers.find((p) => p.available);
    if (!provider) {
      SpotToast.error("No provider available for this integration");
      return;
    }

    setIsConnecting(true);
    const baseUrl = EnvService.getVar("FRONTEND_URL");
    const returnTo = `${baseUrl}${pathname}`;

    try {
      const response = await IntegrationsService.oauthStart({
        path: { group_id: spotGroup.id },
        body: {
          integration_type: info.type,
          provider_key: provider.key,
          redirect_uri: returnTo,
        },
      });
      const authorizeUrl = (response.data as { authorize_url?: string })
        ?.authorize_url;
      if (authorizeUrl) {
        router.push(authorizeUrl);
      } else {
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
        SpotToast.success(`${sourceConfig.name} connected!`);
      }
    } catch {
      SpotToast.error("Failed to connect integration");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleNext = (
    override?: InboxSource | React.MouseEvent | "skip_context"
  ) => {
    const sourceToUse =
      typeof override === "string" && override !== "skip_context"
        ? (override as InboxSource)
        : override === null
          ? null
          : state.source;

    const isConnected = isSourceConnected(sourceToUse, integrations || null);

    if (currentStep === "source" && sourceToUse !== null && !isConnected) {
      handleConnect(sourceToUse);
      return;
    }

    let nextIndex = currentStepIndex + 1;

    // Check if we should skip source_config
    if (WIZARD_STEPS[nextIndex] === "source_config") {
      const sourceConfig = INBOX_SOURCES.find(
        (s: InboxSourceConfig) => s.id === sourceToUse
      );
      if (!sourceConfig?.hasConfiguration) {
        nextIndex++;
      }
    }

    if (override === "skip_context" && WIZARD_STEPS[nextIndex] === "context") {
      nextIndex++;
    }

    if (nextIndex < WIZARD_STEPS.length) {
      if (WIZARD_STEPS[nextIndex] === "done") {
        setCurrentStep("done");
        handleCreate(override === "skip_context");
        return;
      }
      setCurrentStep(WIZARD_STEPS[nextIndex]);
    }
  };

  const handleBack = () => {
    let prevIndex = currentStepIndex - 1;

    // Skip source_config if source has no configuration
    if (WIZARD_STEPS[prevIndex] === "source_config") {
      const sourceConfig = INBOX_SOURCES.find(
        (s: InboxSourceConfig) => s.id === state.source
      );
      if (!sourceConfig?.hasConfiguration) {
        prevIndex--;
      }
    }

    // Skip context step if no use case is selected
    if (WIZARD_STEPS[prevIndex] === "context" && !state.useCase) {
      prevIndex--;
    }

    if (prevIndex >= 0) {
      setCurrentStep(WIZARD_STEPS[prevIndex]);
    }
  };

  const handleSourceSelect = (source: InboxSource) => {
    setState((prev) => ({ ...prev, source }));
  };

  const handleSourceConfigChange = (sourceConfig: Record<string, any>) => {
    setState((prev) => ({ ...prev, sourceConfig }));
  };

  const handleUseCaseSelect = (useCase: InboxUseCase | null) => {
    if (!useCase) {
      setState((prev) => ({
        ...prev,
        useCase: null,
        context: {},
      }));
      templateForm.reset({});
      setTemplateConfigData(null);
      return;
    }

    setState((prev) => ({
      ...prev,
      useCase,
      context: {},
    }));
    templateForm.reset({});

    // Pre-fetch config while still on use_case step
    if (spotGroup?.id) {
      setIsLoadingConfig(true);
      setTemplateConfigData(null);
      axios
        .post(
          `/api/backend/groups/${spotGroup.id}/inbox-templates/${useCase}/config`,
          {}
        )
        .then((response) => {
          setTemplateConfigData(response.data);

          // Set initial form values from config defaults
          if (response.data?.config?.root) {
            const rootConfig = response.data.config.root;
            Object.entries(rootConfig).forEach(
              ([key, fieldConfig]: [string, any]) => {
                if (fieldConfig?.default !== undefined) {
                  templateForm.setValue(key, fieldConfig.default);
                }
              }
            );
          }

          // Now advance to context step
          setCurrentStep("context");
        })
        .catch(() => {
          SpotToast.error("Failed to load template configuration");
        })
        .finally(() => {
          setIsLoadingConfig(false);
        });
    }
  };

  const handleCreate = async (fromScratch?: boolean) => {
    const sourceConfig = state.source
      ? INBOX_SOURCES.find((s: InboxSourceConfig) => s.id === state.source)
      : null;

    // Template deploy flow
    if (!fromScratch && state.useCase && spotGroup?.id) {
      setIsDeploying(true);
      try {
        const formValues = templateForm.getValues();

        const response = await axios.post(
          `/api/backend/groups/${spotGroup.id}/inbox-templates/${state.useCase}/deploy`,
          {
            config: formValues,
            source: state.source,
            source_config: state.source === "noxus" ? state.sourceConfig : {},
          }
        );

        const refs = response.data;
        const inboxRef = refs?.find((r: any) => r.type === "inbox");
        if (inboxRef?.reference) {
          setCreatedInboxId(inboxRef.reference);
        }
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.INBOXES],
        });
      } catch {
        SpotToast.error("Failed to create inbox from template");
        // Go back to previous step on failure
        handleBack();
      } finally {
        setIsDeploying(false);
      }
      return;
    }

    // Start from scratch flow
    const inboxName = `Inbox${sourceConfig ? ` - ${sourceConfig.name}` : ""}`;

    createInbox(
      {
        name: inboxName,
        description: "A custom inbox created from scratch.",
        meta: {
          source: state.source,
          source_config: state.source === "noxus" ? state.sourceConfig : {},
          useCase: null,
          conversational: sourceConfig?.conversational || false,
          transportType: sourceConfig?.conversational ? state.source : null,
        },
      },
      {
        onSuccess: async (inbox) => {
          if (inbox?.id) {
            setCreatedInboxId(inbox.id);
          }
        },
      }
    );
  };

  const handleClose = () => {
    setState(getInitialState());
    setCurrentStep("intro");
    templateForm.reset({});
    setTemplateConfigData(null);
    setCreatedInboxId(null);
    onClose();
  };

  const getButtonLabel = () => {
    if (currentStep === "source" && state.source && !sourceConnected) {
      return isConnecting ? "Connecting..." : "Connect & Continue";
    }
    return "Continue";
  };

  const visibleSetupSteps = useMemo((): WizardStep[] => {
    const sourceIndex = WIZARD_STEPS.indexOf("source");
    return WIZARD_STEPS.slice(sourceIndex).filter((step) => step !== "done");
  }, []);

  const currentSetupStepIndex = visibleSetupSteps.indexOf(currentStep);
  const isSetupStep = currentSetupStepIndex !== -1;

  const renderStep = () => {
    switch (currentStep) {
      case "intro":
        return (
          <IntroStep
            onNext={handleNext}
            onSkip={() => setCurrentStep("source")}
          />
        );
      case "hiw_1":
        return <HowItWorksStep1 onNext={handleNext} onBack={handleBack} />;
      case "hiw_2":
        return <HowItWorksStep2 onNext={handleNext} onBack={handleBack} />;
      case "hiw_3":
        return <HowItWorksStep3 onNext={handleNext} onBack={handleBack} />;
      case "source":
        return (
          <SourceStep
            selected={state.source}
            onSelect={handleSourceSelect}
            integrations={integrations || null}
            onNext={handleNext}
            onBack={handleBack}
            canProceed={canProceed && !isConnecting}
            nextLabel={getButtonLabel()}
          />
        );
      case "source_config":
        return (
          <SourceConfigStep
            source={state.source}
            config={state.sourceConfig}
            onChange={handleSourceConfigChange}
            onNext={handleNext}
            onBack={handleBack}
            canProceed={canProceed}
            slugError={slugError}
          />
        );
      case "use_case":
        return (
          <UseCaseStep
            onSelect={handleUseCaseSelect}
            onNext={handleNext}
            onBack={handleBack}
            canProceed={canProceed}
            isLoading={isLoadingConfig}
            loadingUseCase={state.useCase}
          />
        );
      case "context":
        return state.useCase ? (
          <ContextStep
            useCase={state.useCase}
            form={templateForm}
            configData={templateConfigData}
            onConfigDataChange={setTemplateConfigData}
            onNext={handleNext}
            onBack={handleBack}
            canProceed={canProceed}
          />
        ) : null;
      // case "review":
      //   return (
      //     <ReviewStep
      //       state={state}
      //       onNext={handleNext}
      //       onBack={handleBack}
      //       canProceed={canProceed && !isCreating && !isDeploying}
      //       nextLabel={getButtonLabel()}
      //       isDeploying={isDeploying || isCreating}
      //     />
      //   );
      case "done":
        return (
          <DoneStep
            email={
              state.source === "noxus" && state.sourceConfig.slug
                ? `${state.sourceConfig.slug}@${EnvService.getVar("INBOX_DOMAIN") || "inbox.noxus.ai"}`
                : null
            }
            isDeploying={isDeploying || isCreating}
            onGoToInbox={() => {
              if (createdInboxId) {
                router.push(`/inbox/${createdInboxId}`);
              }
              handleClose();
            }}
            onClose={handleClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        size="3xl"
        showCloseButton={false}
        className="bg-background flex flex-col"
      >
        <DialogTitle className="sr-only">Create new inbox</DialogTitle>
        <DialogDescription className="sr-only">
          Follow the steps to set up your new inbox and automate your workflow.
        </DialogDescription>
        {/* Custom Header elements */}
        <div className="absolute top-[15px] right-[15px] z-50">
          <button
            onClick={handleClose}
            className="p-1 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="absolute top-[15px] left-[15px] z-50">
          {isSetupStep && (
            <div className="bg-[#fcfcfc] rounded-[4px] px-2 py-1 flex items-center justify-center">
              <span className="text-[12px] leading-4 font-normal text-[#c1c4c9] whitespace-nowrap">
                Step {currentSetupStepIndex + 1} of {visibleSetupSteps.length}
              </span>
            </div>
          )}
        </div>

        <div className="h-[520px] max-h-[78vh] flex flex-col relative p-3 pt-6 overflow-hidden">
          {renderStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
